import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Link,
  LinearProgress,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CelebrationIcon from '@mui/icons-material/Celebration';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useDemoRole } from '../../contexts/DemoRoleContext';
import { useTour } from '../../tours/TourProvider';
import { hasTour } from '../../tours/tours';
import { useOnboarding } from '../../onboarding/OnboardingProvider';
import { OPEN_WELCOME_EVENT, TOUR_EVENT } from '../../onboarding/onboardingEvents';
import { ONBOARDING_STEP_DEFS, StepIcon } from '../../onboarding/steps';

// Re-export for existing consumers (BetaBanner).
export { OPEN_WELCOME_EVENT };

// Steps (titles/descriptions are Tim's — D02) come from the shared registry.
const STEPS = ONBOARDING_STEP_DEFS;

/**
 * WelcomeChecklist — "five steps to see what it can do" dialog.
 *
 * Progress comes from OnboardingProvider (Firestore-backed): a step completes
 * when its tour is finished (FINISH) or when the user actually does the thing
 * (filters to a site, submits a survey, adds a member, approves an import).
 * Go launches the matching guided tour.
 *
 * Opening: OnboardingProvider opens this once after the first-login tour
 * ends; afterwards it's re-openable from the BetaBanner "Getting started"
 * link and from /help.
 */
export default function WelcomeChecklist() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { startTour } = useTour();
  const { steps } = useOnboarding();
  const { canViewAs } = useDemoRole();
  const uid = user?.uid || null;

  const [open, setOpen] = useState(false);
  // Tour we launched from a Go/Replay button — when IT ends, reopen the
  // checklist so the user sees their progress (previously the checklist only
  // auto-opened once ever, so post-tour it just… never came back).
  const reopenAfterTourRef = useRef(null);

  // Openable from anywhere (BetaBanner link, OnboardingProvider after tour 1).
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener(OPEN_WELCOME_EVENT, handler);
    return () => window.removeEventListener(OPEN_WELCOME_EVENT, handler);
  }, []);

  // Reopen after a checklist-launched tour finishes (completed or skipped).
  useEffect(() => {
    const handler = (e) => {
      const { type, key } = e.detail || {};
      if (
        (type === 'completed' || type === 'skipped') &&
        key && key === reopenAfterTourRef.current
      ) {
        reopenAfterTourRef.current = null;
        setTimeout(() => setOpen(true), 500);
      }
    };
    window.addEventListener(TOUR_EVENT, handler);
    return () => window.removeEventListener(TOUR_EVENT, handler);
  }, []);

  const handleGo = (step) => {
    setOpen(false);
    if (hasTour(step.key)) {
      reopenAfterTourRef.current = step.key;
      startTour(step.key);
    } else {
      navigate(step.route);
    }
  };

  const handleDismiss = () => setOpen(false);

  const doneCount = STEPS.filter((s) => steps[s.key]).length;
  const allDone = doneCount === STEPS.length;

  // The checklist is the org-admin (beta tester) journey — scoped members
  // don't get steps like "add your people" or "import your org".
  if (!uid || !canViewAs) return null;

  return (
    <Dialog open={open} onClose={handleDismiss} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle sx={{ pb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" component="div" fontWeight={800}>
              Welcome to REPSit.ai
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              We&rsquo;ve provided you with a preloaded, sandboxed (isolated) organization with
              pre-populated data to serve as your initial entry into the world of Principle-Based
              Intelligent Assistance.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              Each step below offers quick insight into each of the major functions. We recommend
              that you complete each step in order to familiarize yourself with the capabilities of
              REPSit.ai.
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleDismiss} aria-label="Close welcome checklist">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, mt: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {doneCount} of {STEPS.length} done
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(doneCount / STEPS.length) * 100}
            sx={{ height: 6, borderRadius: 3 }}
          />
          {allDone && (
            <Typography
              variant="body2"
              sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.75, color: 'success.main', fontWeight: 600 }}
            >
              <CelebrationIcon fontSize="small" />
              You&rsquo;ve done the whole core loop — now break things and tell us what you find.
            </Typography>
          )}
        </Box>
        <Stack spacing={1.25}>
          {STEPS.map((step) => {
            const done = Boolean(steps[step.key]);
            return (
              <Box
                key={step.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.25,
                  border: 1,
                  borderColor: done ? 'success.light' : 'divider',
                  borderRadius: 2,
                  bgcolor: done ? 'rgba(76, 175, 80, 0.06)' : 'transparent',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  {done ? <CheckCircleIcon color="success" /> : <StepIcon step={step} />}
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700}>
                    {step.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {step.description}
                    {step.guide && (
                      <>
                        {' · '}
                        <Link href={step.guide} target="_blank" rel="noopener noreferrer" underline="hover">Guide</Link>
                      </>
                    )}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant={done ? 'text' : 'contained'}
                  onClick={() => handleGo(step)}
                  sx={{ flexShrink: 0, minWidth: 48 }}
                >
                  {done ? 'Replay' : 'Go'}
                </Button>
              </Box>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          color="primary"
          onClick={() => {
            handleDismiss();
            navigate('/help');
          }}
          sx={{ textTransform: 'none', mr: 'auto' }}
        >
          Help &amp; Docs
        </Button>
        <Button color="inherit" onClick={handleDismiss} sx={{ textTransform: 'none' }}>
          I&rsquo;ll explore on my own
        </Button>
      </DialogActions>
    </Dialog>
  );
}
